import { DesignRuleValidationIssue } from 'data/types';
import {
  useDeleteStructureStepCompletedMutation,
  useSaveStructureStepCompletedMutation
} from 'features/api/project-api';
import {
  addManuallyCompletedStep,
  removeManuallyCompletedStep
} from 'features/ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
  useStore
} from 'react-redux';
import { getAddSpacesLevelStepId } from 'utils/completed-steps-helpers';
import { addSpacesRequirementsMet } from 'utils/structure-add-spaces';
import { isErrorSeverity } from 'utils/validation';
import { AppDispatch, RootState } from './store';

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppStore = () => useStore<RootState>();

export function useStructureSaveQueue() {
  const [isProcessing, setIsProcessing] = useState(false);
  const pendingOperationRef = useRef<(() => Promise<void>) | null>(null);
  const isProcessingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (pendingOperationRef.current) {
      const operation = pendingOperationRef.current;
      pendingOperationRef.current = null;

      try {
        await operation();
      } catch (error) {
        console.error('Save queue operation failed:', error);
      }

      if (pendingOperationRef.current) {
        await processQueue();
      } else {
        isProcessingRef.current = false;
        setIsProcessing(false);
      }
    } else {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  }, []);

  const queueSave = useCallback(
    async (operation: () => Promise<void>) => {
      if (isProcessingRef.current) {
        pendingOperationRef.current = operation;
      } else {
        isProcessingRef.current = true;
        setIsProcessing(true);

        try {
          await operation();
        } catch (error) {
          console.error('Save queue operation failed:', error);
        }

        await processQueue();
      }
    },
    [processQueue]
  );

  const hasPendingOperations = useCallback(
    () => pendingOperationRef.current !== null,
    []
  );

  const isBusy = useCallback(
    () => isProcessingRef.current || pendingOperationRef.current !== null,
    []
  );

  return {
    queueSave,
    isProcessing,
    hasPendingOperations,
    isBusy
  };
}

export function stepLabelForSection(sectionId: string): string | null {
  if (sectionId === 'condensers') return 'Condensers';
  if (sectionId.startsWith('add_spaces_zone_')) return 'Zones';
  if (sectionId === 'air_handlers') return 'Air Handlers';
  if (sectionId === 'line_sets') return 'Line Sets';
  return null;
}

const HIDDEN_RULE_CODES = new Set<string>([]);

const issueAffectsZone = (
  issue: DesignRuleValidationIssue,
  zoneUniqueId: string
): boolean => issue.failures.some((f) => f.zoneUniqueId === zoneUniqueId);

const issueAffectsCondenser = (
  issue: DesignRuleValidationIssue,
  condenserUniqueId: string
): boolean =>
  issue.failures.some((f) => f.condenserUniqueId === condenserUniqueId);

function summarize(issues: DesignRuleValidationIssue[]) {
  const errors = issues.filter((i) => isErrorSeverity(i.severity));
  const warnings = issues.filter((i) => !isErrorSeverity(i.severity));
  return {
    isValid: errors.length === 0,
    hasWarning: warnings.length > 0,
    messages: [
      ...errors.map((i) => `Error: ${i.message}`),
      ...warnings.map((i) => `Warning: ${i.message}`)
    ]
  };
}

export function useConfigurationValidation(currentSectionId: string) {
  const { designRuleResults } = useAppSelector((state) => state.configurations);

  const currentStepLabel = useMemo(
    () => stepLabelForSection(currentSectionId),
    [currentSectionId]
  );

  const issuesForSection = useMemo(() => {
    const filtered = designRuleResults.filter(
      (i) => !HIDDEN_RULE_CODES.has(i.code)
    );
    if (!currentStepLabel) return filtered;
    return filtered.filter(
      (i) => !i.steps?.length || i.steps.includes(currentStepLabel)
    );
  }, [designRuleResults, currentStepLabel]);

  const errors = useMemo(
    () => issuesForSection.filter((i) => isErrorSeverity(i.severity)),
    [issuesForSection]
  );

  const warnings = useMemo(
    () => issuesForSection.filter((i) => !isErrorSeverity(i.severity)),
    [issuesForSection]
  );

  const getCondenserValidation = useCallback(
    (condenserUniqueId: string) => {
      const condenserIssues = issuesForSection.filter((i) =>
        issueAffectsCondenser(i, condenserUniqueId)
      );
      return summarize(condenserIssues);
    },
    [issuesForSection]
  );

  const getZoneValidation = useCallback(
    (zoneUniqueId: string) => {
      const zoneIssues = issuesForSection.filter((i) =>
        issueAffectsZone(i, zoneUniqueId)
      );
      return summarize(zoneIssues);
    },
    [issuesForSection]
  );

  return {
    getCondenserValidation,
    getZoneValidation,
    errors,
    warnings,
    issues: issuesForSection,
    isValid: errors.length === 0,
    hasWarnings: warnings.length > 0
  };
}

/**
 * Hook for managing Step 3 (base_layout) completion status
 * when levels are added or removed.
 */
export function useBaseLayoutStepCompletion() {
  const dispatch = useAppDispatch();
  const { structureUniqueId, structure } = useAppSelector(
    (state) => state.structure
  );
  const { manuallyCompletedSteps } = useAppSelector((state) => state.ui);
  const [saveStepCompleted] = useSaveStructureStepCompletedMutation();
  const [deleteStepCompleted] = useDeleteStructureStepCompletedMutation();

  const onLevelAdded = useCallback(() => {
    dispatch(removeManuallyCompletedStep('base_layout'));
    if (structureUniqueId) {
      saveStepCompleted({
        structureUniqueId,
        stepCode: 'base_layout',
        subStepCode: null,
        completed: false
      });
    }
  }, [dispatch, structureUniqueId, saveStepCompleted]);

  const onLevelRemoved = useCallback(
    (levelUniqueId: string) => {
      const removedStepId = getAddSpacesLevelStepId(levelUniqueId);
      dispatch(removeManuallyCompletedStep(removedStepId));
      if (structureUniqueId) {
        deleteStepCompleted([
          {
            structureUniqueId,
            stepCode: 'base_layout',
            subStepCode: removedStepId
          }
        ]).catch(() => {});
      }

      const remainingLevels = structure.filter(
        (level) => level.uniqueId !== levelUniqueId
      );

      const baseLayoutComplete =
        addSpacesRequirementsMet(remainingLevels) &&
        manuallyCompletedSteps.includes('orientation');

      dispatch(
        baseLayoutComplete
          ? addManuallyCompletedStep('base_layout')
          : removeManuallyCompletedStep('base_layout')
      );
      if (structureUniqueId) {
        saveStepCompleted({
          structureUniqueId,
          stepCode: 'base_layout',
          subStepCode: null,
          completed: baseLayoutComplete
        });
      }
    },
    [
      dispatch,
      structure,
      manuallyCompletedSteps,
      structureUniqueId,
      saveStepCompleted,
      deleteStepCompleted
    ]
  );

  return {
    onLevelAdded,
    onLevelRemoved
  };
}
